import { useEffect, useMemo, useState } from "react";
import {
  getStatsAlerts,
  getStatsByCatalogue,
  getStatsIssues,
  getStatsOverview,
  getStatsTrend,
} from "@/services/api/guardiansv2/stats";
import type {
  AlertStats,
  CatalogueStat,
  IssueStat,
  OverviewStats,
  StatsFilterParams,
  TrendResponse,
} from "@/services/api/guardiansv2/types";
import { guardiansErrorMessage } from "../caughtError";

export interface UseGuardiansStatsResult {
  overview: OverviewStats | null;
  byCatalogue: CatalogueStat[];
  issues: IssueStat[];
  trend: TrendResponse | null;
  alerts: AlertStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useGuardiansStats(
  filters: StatsFilterParams,
  { enabled = true }: { enabled?: boolean } = {}
): UseGuardiansStatsResult {
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [byCatalogue, setByCatalogue] = useState<CatalogueStat[]>([]);
  const [issues, setIssues] = useState<IssueStat[]>([]);
  const [trend, setTrend] = useState<TrendResponse | null>(null);
  const [alerts, setAlerts] = useState<AlertStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const [ov, cat, iss, tr, al] = await Promise.all([
          getStatsOverview(filters, controller.signal),
          getStatsByCatalogue(filters, controller.signal),
          getStatsIssues(filters, controller.signal),
          getStatsTrend(filters, controller.signal),
          getStatsAlerts(filters, controller.signal),
        ]);
        if (controller.signal.aborted) return;
        setOverview(ov);
        setByCatalogue(cat);
        setIssues(iss);
        setTrend(tr);
        setAlerts(al);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(guardiansErrorMessage(e, "Failed to load stats"));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [enabled, filterKey, filters, refreshToken]);

  const refresh = () => setRefreshToken((n) => n + 1);

  return {
    overview,
    byCatalogue,
    issues,
    trend,
    alerts,
    loading,
    error,
    refresh,
  };
}
